import { useEffect, useMemo, useRef, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  X, Play, Pause, Hash, FileAudio, ShieldAlert, PieChart as PieIcon,
  BarChart3, Globe2, ExternalLink, Handshake, Music,
} from "lucide-react";
import { getReleaseDetail, getSessions, getAnalytics } from "@/lib/afmApi";
import { fmtNum } from "@/lib/format";

const PIE_COLORS = ["#AC3333", "#111827", "#F59E0B", "#2563EB", "#059669", "#7C3AED", "#DB2777"];

const audioFormat = (url) => {
  if (!url) return "No disponible";
  const ext = (url.split("?")[0].split(".").pop() || "").toLowerCase();
  return { wav: "WAV · Máster", flac: "FLAC · Lossless", mp3: "MP3 · 320kbps", aac: "AAC", m4a: "AAC" }[ext] || ext.toUpperCase() || "—";
};

function Waveform({ url }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const bars = useMemo(() => {
    const seed = (url || "track").split("").reduce((a, c) => a + c.charCodeAt(0), 7);
    return Array.from({ length: 60 }, (_, i) => 18 + Math.round(Math.abs(Math.sin(seed * 0.37 + i * 0.55)) * 82));
  }, [url]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  return (
    <div className="flex items-center gap-4" data-testid="track-waveform">
      <button
        data-testid="track-play-button"
        onClick={toggle}
        disabled={!url}
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#AC3333] text-white transition-colors hover:bg-[#FF0505] disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-0.5" />}
      </button>
      <div className="flex h-14 flex-1 items-center gap-[2px] overflow-hidden">
        {bars.map((h, i) => {
          const active = (i / bars.length) * 100 <= progress;
          return <span key={i} className={`w-full rounded-full transition-colors ${active ? "bg-[#AC3333]" : "bg-slate-200"}`} style={{ height: `${h}%` }} />;
        })}
      </div>
      {url && (
        <audio
          ref={ref}
          src={url}
          onTimeUpdate={(e) => { const t = e.target; if (t.duration) setProgress((t.currentTime / t.duration) * 100); }}
          onEnded={() => { setPlaying(false); setProgress(0); }}
          className="hidden"
        />
      )}
    </div>
  );
}

function Donut({ title, data }) {
  if (!data.length) return null;
  return (
    <div className="flex-1" data-testid={`split-donut-${title.toLowerCase()}`}>
      <div className="mb-1 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="relative h-36">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={38} outerRadius={60} paddingAngle={2} stroke="none">
              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-400">100%</div>
      </div>
      <div className="mt-1 space-y-1">
        {data.map((d, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
              <span className="truncate text-slate-600">{d.name}</span>
            </span>
            <span className="font-mono font-semibold text-black">{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId}>
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#AC3333]" />
      <h3 className="text-sm font-bold uppercase tracking-wider text-black">{title}</h3>
    </div>
    {children}
  </div>
);

export default function TrackPanel({ release, userId, onClose, onOpenDetail }) {
  const [detail, setDetail] = useState(null);
  const [session, setSession] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      getReleaseDetail(release.id),
      getSessions(userId),
      getAnalytics(userId, "28d"),
    ]).then(([d, s, a]) => {
      if (!alive) return;
      setDetail(d.status === "fulfilled" ? d.value : null);
      const sessions = s.status === "fulfilled" && Array.isArray(s.value) ? s.value : [];
      setSession(sessions.find((x) => x.release_id === release.id) || null);
      setCountries(a.status === "fulfilled" ? (a.value?.countries || []).slice(0, 6) : []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [release.id, userId]);

  const byStore = detail?.royalties?.by_store || [];
  const maxStore = Math.max(1, ...byStore.map((s) => s.streams || 0));
  const parts = session?.participants || [];
  const masterData = parts.filter((p) => Number(p.master_pct) > 0).map((p) => ({ name: p.name, value: Number(p.master_pct) }));
  const pubData = parts.filter((p) => Number(p.publishing_pct) > 0).map((p) => ({ name: p.name, value: Number(p.publishing_pct) }));

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="track-panel">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start gap-4 border-b border-slate-200 bg-white/90 px-6 py-5 backdrop-blur">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            {release.cover_url ? <img src={release.cover_url} alt={release.title} className="h-full w-full object-cover" /> : <Music className="h-6 w-6 text-slate-400" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-serif text-2xl font-extrabold text-black">{release.title}</h2>
            <p className="truncate text-sm text-slate-500">{release.main_artist || release.artist_name}{release.genre_main ? ` · ${release.genre_main}` : ""}</p>
          </div>
          <button data-testid="track-panel-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-black"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-8 px-6 py-6">
          {/* Ficha técnica */}
          <Section icon={FileAudio} title="Ficha técnica" testId="section-ficha-tecnica">
            <Waveform url={release.audio_url || ""} />
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><Hash className="h-3 w-3" /> ISRC</div>
                <div className="mt-1 truncate font-mono text-xs font-semibold text-black" data-testid="track-isrc">{release.isrc || detail?.isrc || "Por asignar"}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><FileAudio className="h-3 w-3" /> Formato</div>
                <div className="mt-1 truncate text-xs font-semibold text-black" data-testid="track-format">{audioFormat(release.audio_url)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><ShieldAlert className="h-3 w-3" /> Contenido</div>
                <div className="mt-1" data-testid="track-explicit">
                  {release.explicit
                    ? <span className="inline-flex items-center gap-1 rounded bg-black px-1.5 py-0.5 text-[10px] font-bold text-white">EXPLICIT</span>
                    : <span className="text-xs font-semibold text-emerald-600">Clean</span>}
                </div>
              </div>
            </div>
          </Section>

          {/* Split sheet visualizer */}
          <Section icon={PieIcon} title="Reparto · Split Sheet" testId="section-split-visualizer">
            {parts.length > 0 ? (
              <div className="flex gap-4 rounded-xl border border-slate-200 bg-white p-4">
                <Donut title="Master" data={masterData} />
                <div className="w-px bg-slate-100" />
                <Donut title="Publishing" data={pubData} />
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5" data-testid="split-empty-cta">
                <p className="text-sm text-slate-500">Aún no hay un Split Sheet vinculado a este track.</p>
                <button onClick={() => onOpenDetail("/split-sheets")} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-black px-3.5 py-2 text-xs font-semibold text-white hover:bg-slate-800"><Handshake className="h-3.5 w-3.5" /> Crear acuerdo</button>
              </div>
            )}
          </Section>

          {/* Performance rápido */}
          <Section icon={BarChart3} title="Performance rápido" testId="section-performance">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-400"><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-black" /> Cargando rendimiento…</div>
            ) : byStore.length > 0 ? (
              <div className="space-y-5">
                <div className="space-y-2.5" data-testid="performance-by-store">
                  {byStore.map((s) => (
                    <div key={s.key} className="flex items-center gap-3">
                      <span className="w-28 shrink-0 truncate text-xs font-medium text-slate-600">{s.name}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#AC3333]" style={{ width: `${Math.round(((s.streams || 0) / maxStore) * 100)}%` }} />
                      </div>
                      <span className="w-16 shrink-0 text-right font-mono text-xs font-semibold tabular-nums text-black">{fmtNum(s.streams)}</span>
                    </div>
                  ))}
                </div>
                {countries.length > 0 && (
                  <div data-testid="performance-countries">
                    <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400"><Globe2 className="h-3.5 w-3.5" /> Top países · audiencia del catálogo</div>
                    <div className="flex flex-wrap gap-2">
                      {countries.map((c) => (
                        <span key={c.name} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs">
                          <span className="font-medium text-slate-700">{c.name}</span>
                          <span className="font-mono font-semibold text-[#AC3333]">{c.pct}%</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">Las métricas aparecerán cuando el track acumule reproducciones en tiendas.</p>
            )}
          </Section>

          <button onClick={() => onOpenDetail(`/catalog/${release.id}`)} data-testid="track-panel-full-detail" className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-black hover:text-black">
            <ExternalLink className="h-4 w-4" /> Ver ficha completa del lanzamiento
          </button>
        </div>
      </div>
    </div>
  );
}
